import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  createDesignHistory,
  recordDesignHistory,
  redoDesignHistory,
  undoDesignHistory,
  type DesignHistory,
} from './designHistory';

/** Call ready only after the initial working set is restored; mount a new hook for a new analysis. */
export const useDesignHistory = <T>(
  value: T,
  restore: (value: T, reason: 'history') => T | void | null,
  ready: boolean
) => {
  const history = useRef<DesignHistory<T> | null>(null);
  const fingerprint = JSON.stringify(value);
  const previousFingerprint = useRef<string | undefined>(undefined);
  const savedFingerprint = useRef<string | undefined>(undefined);
  const interaction = useRef(0);
  /** A deferred follow-up change files under the step that caused it. */
  const fileUnder = useRef<number | null>(null);
  /**
   * The render after Undo/Redo may normalise the restored value (derived
   * provenance): that is the same step, not a new
   * edit, so it replaces the present and keeps Redo.
   */
  const settlingTravel = useRef(false);
  const [, render] = useState(0);

  useEffect(() => {
    const beginInteraction = () => {
      interaction.current += 1;
    };
    document.addEventListener('pointerdown', beginInteraction, true);
    document.addEventListener('keydown', beginInteraction, true);
    return () => {
      document.removeEventListener('pointerdown', beginInteraction, true);
      document.removeEventListener('keydown', beginInteraction, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!ready || fingerprint === previousFingerprint.current) return;
    if (!history.current) {
      history.current = createDesignHistory(value);
      savedFingerprint.current = fingerprint;
    } else if (settlingTravel.current) {
      history.current = { ...history.current, present: value };
    } else {
      history.current = recordDesignHistory(
        history.current,
        value,
        fileUnder.current ?? interaction.current
      );
    }
    fileUnder.current = null;
    previousFingerprint.current = fingerprint;
    render((count) => count + 1);
  }, [fingerprint, ready, value]);
  // A follow-up that changed nothing must not capture a later change.
  useLayoutEffect(() => {
    fileUnder.current = null;
    settlingTravel.current = false;
  });

  const travel = useCallback(
    (direction: 'undo' | 'redo') => {
      if (!ready || !history.current) return;
      const travelled =
        direction === 'undo'
          ? undoDesignHistory(history.current)
          : redoDesignHistory(history.current);
      const restored = restore(travelled.present, 'history');
      if (restored === null) return;
      history.current = {
        ...travelled,
        present: restored ?? travelled.present,
      };
      previousFingerprint.current = JSON.stringify(history.current.present);
      settlingTravel.current = true;
      render((count) => count + 1);
    },
    [ready, restore]
  );

  const markSaved = useCallback((saved: T) => {
    savedFingerprint.current = JSON.stringify(saved);
    render((count) => count + 1);
  }, []);

  // Google Maps polygon events do not bubble through the document, so callers
  // can explicitly mark those gestures as a new history interaction.
  const recordInteraction = useCallback(() => {
    interaction.current += 1;
  }, []);

  /** The current Undo step, to file a deferred follow-up change under. */
  const currentInteraction = useCallback(() => interaction.current, []);
  /**
   * Files the next recorded change under `step` instead of the current one,
   * so a deferred follow-up (auto-repack) joins the edit that caused it even
   * when a click, Tab or Escape started a new step in between.
   */
  const recordUnder = useCallback((step: number) => {
    fileUnder.current = step;
  }, []);

  return {
    currentInteraction,
    recordUnder,
    canUndo: ready && (history.current?.past.length ?? 0) > 0,
    canRedo: ready && (history.current?.future.length ?? 0) > 0,
    isDirty:
      ready &&
      savedFingerprint.current !== undefined &&
      savedFingerprint.current !== fingerprint,
    undo: () => travel('undo'),
    redo: () => travel('redo'),
    markSaved,
    recordInteraction,
  };
};
